/**
 * Presentation-layer cleanup for agent report / audit bodies.
 *
 * Agent reports occasionally surface internal plumbing — tool identifiers
 * (`ad_performance_fetch`), status tokens (`available: false`), run-log lines
 * (`Recorded: 1 EXCEPTION checkpoint`) — and format them as inline code, which
 * renders as scattered monospace and reads as jargon. A practice owner should
 * see plain, single-font prose. This:
 *   1. unwraps inline `code` so the body is one consistent font,
 *   2. rewrites the well-known internal terms to plain language,
 *   3. drops pure run-log lines,
 * without touching the report's actual substance. It is a safety net over the
 * source-level fix (agents writing owner-facing prose); both keep the inbox
 * readable.
 */

// Internal identifiers, status tokens and jargon → plain phrases. Order matters:
// multi-word phrases must precede the single words they contain.
const TOKEN_REPLACEMENTS: Array<[RegExp, string]> = [
  // Tool / source identifiers.
  [/\bad[_\s]?performance[_\s]?fetch\b/gi, 'the ad performance data'],
  [/\bad[_\s]?strategy[_\s]?fetch\b/gi, 'the Ad Strategy'],
  [/\bcampaign[_\s]?spec[_\s]?fetch\b/gi, 'the saved campaign setup'],
  [/\bdata[_\s]?reachability[_\s]?read\b/gi, 'the data-connection check'],
  [/\bwrite[_\s]?audit[_\s]?report\b/gi, 'the report'],
  [/\bemit[_\s]?checkpoint\b/gi, 'a note'],
  [/\bapproved[_\s]?strategy[_\s]?missing\b/gi, 'no approved strategy on file'],
  [/\bperformance[_\s]?data[_\s]?gap\b/gi, 'a performance-data gap'],
  // Status tokens.
  [/\bavailable\s*[:=]\s*false\b/gi, 'not connected'],
  [/\bavailable\s*[:=]\s*true\b/gi, 'connected'],
  [/\bstrategy\s*[:=]\s*null\b/gi, 'no strategy on file'],
  [/\bcampaigns\s*[:=]\s*\[\]/gi, 'no campaigns found'],
  // Config / infra jargon.
  [/\bBACKEND_URL is not configured\b/gi, "the connection isn't set up"],
  [/\bBACKEND_URL\b/g, 'the data connection'],
  // Process jargon a practice owner would not recognize.
  [/\bEXCEPTION\s+checkpoints\b/gi, 'notes'],
  [/\ban\s+EXCEPTION\s+checkpoint\b/gi, 'a note'],
  [/\bEXCEPTION\s+checkpoint\b/gi, 'a note'],
  [/\bEXCEPTION\b/g, 'a note'],
  [/\bdata[-\s]?completeness\b/gi, 'the data check'],
  [/\bdataComplete\b/g, 'the data check'],
  [/\bperformance[-\s]?reconstructed baseline\b/gi, 'a baseline from past performance'],
  [/\bload[-\s]?bearing tools?\b/gi, 'key data sources'],
  [/\bload[-\s]?bearing inputs?\b/gi, 'key inputs'],
  [/\bload[-\s]?bearing\b/gi, 'key'],
  [/\binput tiers?\b/gi, 'sources of information'],
  [/\bin[-\s]?envelope adjustments?\b/gi, 'changes within the current plan'],
  [/\bamendment requests?\b/gi, 'a request to change the plan'],
  [/\bops notifications?\b/gi, 'an alert to our team'],
];

// Whole lines that are pure run-log / telemetry — dropped entirely.
const DROP_LINE = [
  /^\s*finding\s+\d+\s*:\s*source\b/i,
  /^\s*Recorded\s*:\s*\d+\s+(?:EXCEPTION|note)\b/i,
  /\brequires a query\b/i,
];

// Leading log-level prefixes stripped from an otherwise useful line (kept). The
// combined form must precede the single tokens so it wins the alternation.
const STRIP_PREFIX = /^\s*(EXCEPTION\/ROUTINE|EXCEPTION|ROUTINE)\s*:\s*/i;

/** Remove inline-code backticks so the body renders in one consistent font. */
function unwrapInlineCode(line: string): string {
  return line
    .replace(/`([^`]+)`/g, '$1') // `x` → x
    .replace(/`/g, ''); // stray backticks
}

// A parenthetical holding a camelCase or snake_case field name is the read's plumbing showing
// through, e.g. "(daysWithUpload 0 of businessDays 5)": the sentence already says it in words.
const INTERNAL_PARENTHETICAL = /[ \t]*\((?=[^)\n]*\b(?:[a-z]+[A-Z][A-Za-z]*|[a-z]+_[a-z_]+)\b)[^)\n]*\)/g;

export function sanitizeReportText(input: string | undefined | null): string {
  if (!input) return '';
  const lines = input.split('\n');
  const kept: string[] = [];
  for (const raw of lines) {
    if (DROP_LINE.some((re) => re.test(raw))) continue;
    let line = unwrapInlineCode(raw.replace(STRIP_PREFIX, ''));
    line = line.replace(INTERNAL_PARENTHETICAL, '');
    for (const [re, to] of TOKEN_REPLACEMENTS) line = line.replace(re, to);
    kept.push(line);
  }
  // Collapse any runs of blank lines the drops may have created, and remove em dashes from reports
  // saved before agent-suite started removing them (a line-opening dash becomes a list hyphen).
  return kept
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^([ \t]*)\u2014[ \t]*/gm, '$1- ')
    .replace(/[ \t]*\u2014[ \t]*/g, ', ')
    .trim();
}
